import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Volume2, Radio } from "lucide-react";
import { STATIONS, useStationPlayer } from "@/components/cef/phoneMusic";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* In-car radio HUD (CEF) — /cef/radio/:serverId. Transparent overlay pinned to the
   bottom of the screen: the Boombox dial with REAL audio, synthesized live by the
   shared engine (phoneMusic.js) — zero samples, royalty-free by design.
   Keyboard (bridge maps wheel/buttons): ←/→ station · ↑/↓ volume · space play-pause. */

export default function RadioCefPage() {
  const [params] = useSearchParams();
  const demo = !params.get("token");
  const [idx, setIdx] = useState(0);
  const [trackIdx, setTrackIdx] = useState(0);
  const [vol, setVol] = useState(0.8);
  const { playingId, play, stop, setVolume } = useStationPlayer();
  const station = STATIONS[idx];
  const playing = playingId === station.id;
  const trackTimer = useRef(null);

  // CEF overlay — the 3D world must show through
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  // CEF autoplay policies: resume the context on the first real input
  useEffect(() => {
    const unlock = () => play(STATIONS[idx].id, vol);
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { play(station.id, vol); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (playing) trackTimer.current = setInterval(() => setTrackIdx((i) => i + 1), 9000);
    return () => clearInterval(trackTimer.current);
  }, [playing, idx]);

  const toggle = () => { if (playing) stop(); else play(station.id, vol); };
  const bump = (d) => { setTrackIdx(0); setIdx((i) => (i + d + STATIONS.length) % STATIONS.length); };
  const onVol = (v) => { setVol(v); setVolume(v); };

  // keyboard / bridge-mapped controls — re-registered each render so closures stay fresh
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") bump(1);
      else if (e.key === "ArrowLeft") bump(-1);
      else if (e.key === "ArrowUp") { e.preventDefault(); onVol(Math.min(1, Math.round((vol + 0.1) * 10) / 10)); }
      else if (e.key === "ArrowDown") { e.preventDefault(); onVol(Math.max(0, Math.round((vol - 0.1) * 10) / 10)); }
      else if (e.key === " ") { e.preventDefault(); toggle(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const track = station.tracks[trackIdx % station.tracks.length];

  return (
    <div data-testid="car-radio" className="min-h-screen bg-transparent relative">
      <style>{`@keyframes phEq{0%,100%{transform:scaleY(0.25)}50%{transform:scaleY(1)}}`}</style>
      {demo && <PreviewRibbon what="car radio" />}
      <div className="absolute bottom-8 inset-x-0 flex justify-center pointer-events-none">
        <div data-testid="radio-hud" className="pointer-events-auto flex items-center gap-4 rounded-full bg-black/70 backdrop-blur-xl border border-white/15 pl-5 pr-4 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
          style={{ animation: "phAppIn 0.3s cubic-bezier(0.2, 0.9, 0.3, 1) both" }}>
          {/* equaliser */}
          <div className="flex items-end gap-0.5 h-7 w-12 shrink-0">
            {Array.from({ length: 7 }, (_, i) => (
              <span key={i} className="flex-1 rounded-sm origin-bottom"
                style={{ backgroundColor: station.color, opacity: playing ? 0.9 : 0.2,
                         animation: playing ? `phEq ${0.45 + (i % 4) * 0.12}s ease-in-out ${i * 0.06}s infinite` : "none",
                         height: "100%", transform: playing ? undefined : "scaleY(0.15)" }} />
            ))}
          </div>
          <div className="min-w-0">
            <p data-testid="car-radio-station" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/90 leading-tight">
              <span className="font-bold" style={{ color: station.color }}>{station.freq}</span> · {station.name}
            </p>
            <p data-testid="car-radio-track" className="font-mono text-[8px] text-white/45 truncate max-w-44 leading-tight">
              {playing ? `♪ ${track}` : "— radio off —"}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button data-testid="car-radio-prev" onClick={() => bump(-1)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"><SkipBack className="w-3.5 h-3.5" /></button>
            <button data-testid="car-radio-toggle" onClick={toggle} className="w-10 h-10 rounded-full flex items-center justify-center text-black transition-transform active:scale-90" style={{ backgroundColor: station.color }}>
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <button data-testid="car-radio-next" onClick={() => bump(1)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"><SkipForward className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex items-center gap-2 w-24">
            <Volume2 className="w-3.5 h-3.5 text-white/40 shrink-0" />
            <input data-testid="car-radio-volume" type="range" min="0" max="1" step="0.05" value={vol}
              onChange={(e) => onVol(parseFloat(e.target.value))} className="flex-1 h-1 cursor-pointer" style={{ accentColor: station.color }} />
          </div>
          <Radio className="w-4 h-4 text-white/25 shrink-0" />
        </div>
      </div>
    </div>
  );
}
